import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type SymbolSize = "small" | "medium" | "large";
type SymbolStyle = "official" | "colourful";
export type ChartPalette = "normal" | "night" | "highContrast";

/** MapLibre `encPalette` global-state values used by s57style.json. */
export type EncPalette = "normal" | "night" | "high-contrast";

export function toEncPalette(palette: ChartPalette): EncPalette {
  if (palette === "highContrast") return "high-contrast";
  return palette;
}

export interface ChartSettings {
  symbolSize: SymbolSize;
  symbolStyle: SymbolStyle;
  palette: ChartPalette;
  simplifiedS57: boolean;
  useSiUnits: boolean;
}

interface ChartSettingsContextValue extends ChartSettings {
  setSymbolSize: (size: SymbolSize) => void;
  setSymbolStyle: (style: SymbolStyle) => void;
  setPalette: (palette: ChartPalette) => void;
  setSimplifiedS57: (enabled: boolean) => void;
  setUseSiUnits: (enabled: boolean) => void;
}

const STORAGE_KEY = "chartSettings";

const DEFAULT_SETTINGS: ChartSettings = {
  symbolSize: "medium",
  symbolStyle: "official",
  palette: "normal",
  simplifiedS57: false,
  useSiUnits: false,
};

const ChartSettingsContext = createContext<ChartSettingsContextValue | null>(
  null,
);

function readSettings(): ChartSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<ChartSettings> | null;
    const symbolSize: SymbolSize =
      parsed?.symbolSize === "small" ||
      parsed?.symbolSize === "medium" ||
      parsed?.symbolSize === "large"
        ? parsed.symbolSize
        : DEFAULT_SETTINGS.symbolSize;
    const symbolStyle: SymbolStyle =
      parsed?.symbolStyle === "official" || parsed?.symbolStyle === "colourful"
        ? parsed.symbolStyle
        : DEFAULT_SETTINGS.symbolStyle;
    const palette: ChartPalette =
      parsed?.palette === "normal" ||
      parsed?.palette === "night" ||
      parsed?.palette === "highContrast"
        ? parsed.palette
        : DEFAULT_SETTINGS.palette;
    const simplifiedS57 =
      typeof parsed?.simplifiedS57 === "boolean"
        ? parsed.simplifiedS57
        : DEFAULT_SETTINGS.simplifiedS57;
    const useSiUnits =
      typeof parsed?.useSiUnits === "boolean"
        ? parsed.useSiUnits
        : DEFAULT_SETTINGS.useSiUnits;

    return { symbolSize, symbolStyle, palette, simplifiedS57, useSiUnits };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function ChartSettingsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [settings, setSettings] = useState<ChartSettings>(() => readSettings());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore storage errors
    }
  }, [settings]);

  const setSymbolSize = useCallback((size: SymbolSize) => {
    setSettings((prev) => ({ ...prev, symbolSize: size }));
  }, []);

  const setSymbolStyle = useCallback((style: SymbolStyle) => {
    setSettings((prev) => ({ ...prev, symbolStyle: style }));
  }, []);

  const setPalette = useCallback((palette: ChartPalette) => {
    setSettings((prev) => ({ ...prev, palette }));
  }, []);

  const setSimplifiedS57 = useCallback((enabled: boolean) => {
    setSettings((prev) => ({ ...prev, simplifiedS57: enabled }));
  }, []);

  const setUseSiUnits = useCallback((enabled: boolean) => {
    setSettings((prev) => ({ ...prev, useSiUnits: enabled }));
  }, []);

  const value = useMemo<ChartSettingsContextValue>(
    () => ({
      ...settings,
      setSymbolSize,
      setSymbolStyle,
      setPalette,
      setSimplifiedS57,
      setUseSiUnits,
    }),
    [
      settings,
      setSymbolSize,
      setSymbolStyle,
      setPalette,
      setSimplifiedS57,
      setUseSiUnits,
    ],
  );

  return (
    <ChartSettingsContext.Provider value={value}>
      {children}
    </ChartSettingsContext.Provider>
  );
}

export function useChartSettings(): ChartSettingsContextValue {
  const ctx = useContext(ChartSettingsContext);
  if (!ctx) {
    throw new Error("useChartSettings must be used within ChartSettingsProvider");
  }
  return ctx;
}

